import { TopicGroup, Topic } from '../../types';
import axios from 'axios';
import { TopicRequest } from '../../utils';
import { TopicExportRequest } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/TopicExportRequestBuilder';

export interface GetTopicGroupMetricsResponseRecord {
  topicGroupId: string;
  topicGroupName: string;
  topicId: string;
  topicName: string;
  totalTraffic: number;
  organicTraffic: number;
  paidTraffic: number;
}

export interface GetTopicGroupMetricsResponse {
  records: GetTopicGroupMetricsResponseRecord[];
}

export interface GetTopicMetricsByWeekResponseRecord {
  weekId: number;
  topicId: string;
  topicName: string;
  totalTraffic: number;
  organicTraffic: number;
  paidTraffic: number;
}

export interface GetTopicMetricsByWeekResponse {
  records: GetTopicMetricsByWeekResponseRecord[];
}

export interface BulkGetTopicsResponse {
  [topicId: string]: Topic;
}

export interface GetTopicGroupsContainingTopicsResponseRecord {
  topicId: string;
  topicGroupIds: string[];
}

export interface GetTopicGroupsContainingTopicsResponse {
  records: GetTopicGroupsContainingTopicsResponseRecord[];
}

export interface BulkGetTopicGroupsResponse {
  [topicGroupId: string]: TopicGroup;
}

interface GetTopicCategoriesResponse {
  records: { retailerId: number; categoryId: number; subcategoryId: number }[];
}

export interface GetTopicGroupSummaryPageMetricsResponseRecord {
  topicGroupId: string;
  topicGroupName: string;
  topicId: string;
  topicName: string;
  totalTraffic: number;
  organicTraffic: number;
  paidTraffic: number;
}

export interface GetTopicGroupSummaryPageMetricsResponse {
  records: GetTopicGroupSummaryPageMetricsResponseRecord[];
}

interface GetTopicGroupMetricsByWeekResponseRecord {
  weekId: number;
  topicGroupId: string;
  topicGroupName: string;
  totalTraffic: number;
  organicTraffic: number;
  paidTraffic: number;
}

export interface GetTopicGroupMetricsByWeekResponse {
  records: GetTopicGroupMetricsByWeekResponseRecord[];
}

interface GetSearchTermClassificationResponse {
  records: { searchTerm: string; type: string; brand: string }[];
}

export interface UpdateTopicGroupPayload {
  topicGroupId: string;
  topicGroupName: string;
  includedCategoryIds?: number[];
  excludedCategoryIds?: number[];
  includedSubCategoryIds?: number[];
  excludedSubCategoryIds?: number[];
}

type UpdatedTopicFilters =
  | 'IncludedKeywords'
  | 'ExcludedKeywords'
  | 'IncludedKeywordPhrases'
  | 'ExcludedKeywordPhrases'
  | 'IncludedCategoryIds'
  | 'ExcludedCategoryIds'
  | 'IncludedSubCategoryIds'
  | 'ExcludedSubCategoryIds'
  | 'IncludedBrandIds'
  | 'ExcludedBrandIds';

export interface CreateTopicPayload extends Partial<Record<UpdatedTopicFilters, string[]>> {
  topicName: string;
  retailerId: string;
}

export interface UpdateTopicPayload extends CreateTopicPayload {
  topicId: string;
  beaconClientId: number;
}

interface GetSearchTermClassificationPayload {
  searchTermList: string[];
}

export interface TopicExportResponse {
  requestId: string;
  userId: string;
  appName: string;
  uniqueUserAppKey: string;
  originalRequest: string;
  dataSourceType: string;
  elasticSearchUri: string | null;
  elasticSearchQueries: any[];
  exportColumnsInfo: any[] | null;
  columnDelimiter: string;
  additionalExportOptions: {
    exportRequestSource: string;
  };
  elasticSearchRequests: any[];
  requestedTime: number;
  beganProcessing: number;
  finishedProcessing: number;
  status: string;
  fileUri: string | null;
  fileSize: number;
  fileName: string;
  numberOfDownloads: number;
  lastDownloadTimestamp: number | null;
  isDeleted: boolean;
  error: any | null;
  debug: any | null;
  sqlQueries: any[];
  appStage: string;
  serializedExportRequestJsonFileUrl: string;
  originalRequestFormatVersion: any | null;
}

export interface CreateTopicGroupPayload {
  topicGroupName: string;
  topicIds: string[];
  retailerId: number;
  includedCategoryIds?: number[];
  excludedCategoryIds?: number[];
  includedSubCategoryIds?: number[];
  excludedSubCategoryIds?: number[];
}

interface TopicGroupTaxonomyOptions {
  includedCategoryIds: number[];
  excludedCategoryIds: number[];
  includedSubCategoryIds: number[];
  excludedSubCategoryIds: number[];
}

interface TopicSettingsServerProxy {
  getOwnedTopicGroups: () => Promise<TopicGroup[]>;
  /**
   * Adds a topic to a list of topic groups.
   */
  addTopicToTopicGroups: (payload: {
    topicId: string;
    topicGroupIds: string[];
    taxonomyOptions: TopicGroupTaxonomyOptions;
  }) => Promise<void>;
  /**
   * Adds a list of topics to a topic group.
   */
  addTopicsToTopicGroup: (payload: {
    topicIds: string[];
    topicGroupId: string;
    taxonomyOptions: TopicGroupTaxonomyOptions;
  }) => Promise<void>;
  removeTopicsFromTopicGroup: (payload: {
    topicIds: string[];
    topicGroupId: string;
    taxonomyOptions: TopicGroupTaxonomyOptions;
  }) => Promise<void>;
  getCustomTopics: () => Promise<Topic[]>;
  getOwnedTopics: () => Promise<Topic[]>;
  getTopicGroupMetrics: (payload: TopicRequest) => Promise<GetTopicGroupMetricsResponse>;
  getTopicMetricsByWeek: (payload: TopicRequest) => Promise<GetTopicMetricsByWeekResponse>;
  getTopicGroupMetricsByWeek: (payload: TopicRequest) => Promise<GetTopicGroupMetricsByWeekResponse>;
  getSearchTermClassification: (
    payload: GetSearchTermClassificationPayload
  ) => Promise<GetSearchTermClassificationResponse>;
  getTopicCategories: (topicId: string) => Promise<GetTopicCategoriesResponse>;
  bulkGetTopics: (topicIds: string[]) => Promise<BulkGetTopicsResponse>;
  getTopicGroupsContainingTopics: (payload: {
    retailerId: number;
    topicIds: string[];
  }) => Promise<GetTopicGroupsContainingTopicsResponse>;
  bulkGetTopicGroups: (topicGroupIds: string[]) => Promise<BulkGetTopicGroupsResponse>;
  getTopicGroupSummaryPageMetrics: (payload: TopicRequest) => Promise<GetTopicGroupSummaryPageMetricsResponse>;

  createTopicGroup: (payload: CreateTopicGroupPayload) => Promise<string>;
  updateTopicGroup: (payload: UpdateTopicGroupPayload) => Promise<void>;
  deleteTopicGroup: (topicGroupId: string) => Promise<void>;
  deleteTopic: (topicId: string) => Promise<void>;

  /**
   * Creates a single topic with a list of filters.
   */
  createTopic: (payload: CreateTopicPayload) => Promise<string>;

  /**
   * Updates a single topic with a list of filters.
   */
  updateTopic: (payload: UpdateTopicPayload) => Promise<string>;
  /**
   * Triggers an export request that will be queued in the existing export service
   */
  exportTopic: (payload: TopicExportRequest) => Promise<TopicExportResponse>;

  /**
   * Get topic
   */
  getTopic: (topicId: string) => Promise<Topic>;

  /**
   * Get topic group
   */
  getTopicGroup: (topicId: string) => Promise<TopicGroup>;

  /**
   * Remove a topic from a list of topic groups.
   */
  removeTopicFromMultipleTopicGroups: (payload: {
    topicId: string;
    topicGroupIds: string[];
    taxonomyOptions: TopicGroupTaxonomyOptions;
  }) => Promise<void>;

  /**
   * Invalidates the backend cache for topics
   */
  invalidateCache: () => Promise<void>;
}

export const topicSettingsServerProxy: TopicSettingsServerProxy = {
  getOwnedTopicGroups: async () => {
    const response = await axios.get<TopicGroup[]>('/api/beacontopics/GetOwnedTopicGroups');
    return response.data;
  },

  removeTopicFromMultipleTopicGroups: async ({ topicId, topicGroupIds, taxonomyOptions }) => {
    await axios.post(
      '/api/beacontopics/BulkUpdateTopicGroups',
      topicGroupIds.map((topicGroupId) => ({
        topicGroupId,
        topicIdsToRemove: [topicId],
        ...taxonomyOptions
      }))
    );
  },

  addTopicToTopicGroups: async ({ topicId, topicGroupIds, taxonomyOptions }) => {
    await axios.post(
      '/api/beacontopics/BulkUpdateTopicGroups',
      topicGroupIds.map((topicGroupId) => ({
        topicGroupId,
        topicIdsToAdd: [topicId],
        ...taxonomyOptions
      }))
    );
  },
  addTopicsToTopicGroup: async ({ topicIds, topicGroupId, taxonomyOptions }) => {
    await axios.post('/api/beacontopics/BulkUpdateTopicGroups', [
      {
        topicGroupId,
        topicIdsToAdd: topicIds,
        ...taxonomyOptions
      }
    ]);
  },
  removeTopicsFromTopicGroup: async ({ topicIds, topicGroupId, taxonomyOptions }) => {
    await axios.post('/api/beacontopics/BulkUpdateTopicGroups', [
      {
        topicGroupId,
        topicIdsToRemove: topicIds,
        ...taxonomyOptions
      }
    ]);
  },
  getCustomTopics: async () => {
    const response = await axios.get<Topic[]>('/api/beacontopics/GetCustomTopics');
    return response.data;
  },
  getOwnedTopics: async () => {
    const response = await axios.get<Topic[]>('/api/beacontopics/GetOwnedTopics');
    return response.data;
  },
  getTopicGroupMetrics: async (payload) => {
    const response = await axios.post<GetTopicGroupMetricsResponse>('/api/beacontopics/GetTopicGroupMetrics', payload);
    return response.data;
  },
  getTopicMetricsByWeek: async (payload) => {
    const response = await axios.post<GetTopicMetricsByWeekResponse>(
      '/api/beacontopics/GetTopicMetricsByWeek',
      payload
    );
    return response.data;
  },
  getTopicGroupMetricsByWeek: async (payload) => {
    const response = await axios.post<GetTopicGroupMetricsByWeekResponse>(
      '/api/beacontopics/GetTopicGroupMetricsByWeek',
      payload
    );
    return response.data;
  },

  bulkGetTopics: async (topicIds) => {
    const response = await axios.post<BulkGetTopicsResponse>('/api/beacontopics/BulkGetTopics', topicIds);
    return response.data;
  },
  getTopicCategories: async (topicId) => {
    const response = await axios.get<GetTopicCategoriesResponse>(
      `/api/beacontopics/GetTopicCategories?topicId=${topicId}`
    );
    return response.data;
  },
  getTopicGroupsContainingTopics: async (payload) => {
    const response = await axios.post<GetTopicGroupsContainingTopicsResponse>(
      '/api/beacontopics/GetTopicGroupsContainingTopics',
      payload
    );
    return response.data;
  },
  bulkGetTopicGroups: async (topicGroupIds) => {
    const response = await axios.post<BulkGetTopicGroupsResponse>(
      '/api/beacontopics/BulkGetTopicGroups',
      topicGroupIds
    );
    return response.data;
  },
  getTopicGroupSummaryPageMetrics: async (payload) => {
    const response = await axios.post<GetTopicGroupSummaryPageMetricsResponse>(
      '/api/beacontopics/GetTopicGroupSummaryPageMetrics',
      payload
    );
    return response.data;
  },
  getSearchTermClassification: async (payload) => {
    const response = await axios.post<GetSearchTermClassificationResponse>(
      '/api/beacontopics/GetSearchTermClassification',
      payload
    );
    return response.data;
  },
  createTopicGroup: async (payload: CreateTopicGroupPayload) => {
    const response = await axios.post('/api/beacontopics/CreateTopicGroup', payload);
    return response.data;
  },

  updateTopicGroup: async (payload) => {
    await axios.post('/api/beacontopics/UpdateTopicGroup', payload);
  },
  deleteTopicGroup: async (topicGroupId) => {
    await axios.post(`/api/beacontopics/DeleteTopicGroup?topicGroupId=${topicGroupId}`);
  },
  deleteTopic: async (topicId) => {
    await axios.post('/api/beacontopics/DeleteTopic', { topicId });
  },

  createTopic: async (payload) => {
    const response = await axios.post('/api/beacontopics/CreateTopic', payload);
    return response.data;
  },

  updateTopic: async (payload) => {
    const response = await axios.post('/api/beacontopics/UpdateTopic', payload);
    return response.data;
  },

  exportTopic: async (payload) => {
    const response = await axios.post<TopicExportResponse>('/api/beacontopics/SubmitExportRequest', payload);
    return response.data;
  },

  getTopic: async (topicId) => {
    const response = await axios.get<Topic>(`/api/beacontopics/GetTopic?topicId=${topicId}`);
    return response.data;
  },

  getTopicGroup: async (topicGroupId) => {
    const response = await axios.get<TopicGroup>(`/api/beacontopics/GetTopicGroup?topicGroupId=${topicGroupId}`);
    return response.data;
  },

  invalidateCache: async () => {
    await axios.post('/api/beacontopics/InvalidateCache');
  }
};
